import { Navigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useState } from 'react';
import { cn, hasMessage } from '@/lib/utils';

import { sideViewState, useAuth, useConfig, useChatMessages } from '@chainlit/react-client';

import ElementSideView from '@/components/ElementSideView';
import LeftSidebar from '@/components/LeftSidebar';
import { TaskList } from '@/components/Tasklist';
import { Header } from '@/components/header';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useIsMobile } from '@/hooks/use-mobile';

import { userEnvState } from 'state/user';

type Props = {
  children: JSX.Element;
};

const Page = ({ children }: Props) => {
  const { user, isAuthenticated } = useAuth();
  const userEnv = useRecoilValue(userEnvState);
  const { config } = useConfig();
  const { messages } = useChatMessages();
  const sideView = useRecoilValue(sideViewState);
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Check if welcome screen is active (no messages)
  const isWelcomeScreenActive = !hasMessage(messages);

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(true);
  };

  const handleSidebarRestore = () => {
    setSidebarCollapsed(false);
  };

  if (user && !isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (userEnv.length && !config?.userEnv) {
    return <Navigate to="/env" />;
  }

  // Mobile layout
  if (isMobile) {
    return (
      <SidebarProvider
        defaultOpen={config?.ui.default_sidebar_state !== 'closed'}
      >
        {/* Only show sidebar if welcome screen is not active */}
        {!isWelcomeScreenActive && (
          <LeftSidebar open={mobileOpen} onOpenChange={setMobileOpen} />
        )}
        <div className="flex h-screen w-full">
          <div className="flex flex-col w-full">
            <Header onMobileSidebarToggle={() => setMobileOpen(true)} />
            <div className="flex-1 overflow-hidden">
              <ResizablePanelGroup
                direction="horizontal"
                className="flex flex-row h-full"
              >
                <ResizablePanel
                  className="flex flex-col h-full w-full"
                  minSize={40}
                  defaultSize={sideView ? 70 : 100}
                >
                  <div className="flex flex-row flex-grow overflow-auto">
                    {children}
                  </div>
                </ResizablePanel>
                {sideView ? <ElementSideView /> : <TaskList isMobile={true} />}
              </ResizablePanelGroup>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  // Desktop layout with fixed-width collapsible sidebar
  return (
    <SidebarProvider
      defaultOpen={config?.ui.default_sidebar_state !== 'closed'}
    >
      <div className="flex h-screen w-full">
        {/* Hide sidebar completely when collapsed or welcome screen is active */}
        {sidebarCollapsed || isWelcomeScreenActive ? (
          // Collapsed layout or welcome screen - no sidebar, main content takes full width
          <div className="flex flex-col w-full">
            <Header 
              hasLeftSidebar={false} 
              onSidebarRestore={!isWelcomeScreenActive ? handleSidebarRestore : undefined}
            />
            <div className="flex-1 overflow-hidden">
              <ResizablePanelGroup
                direction="horizontal"
                className="flex flex-row h-full"
              >
                <ResizablePanel
                  className="flex flex-col h-full w-full"
                  minSize={40}
                  defaultSize={sideView ? 70 : 100}
                >
                  <div className="flex flex-row flex-grow overflow-auto">
                    {children}
                  </div>
                </ResizablePanel>
                {sideView ? <ElementSideView /> : <TaskList isMobile={false} />}
              </ResizablePanelGroup>
            </div>
          </div>
        ) : (
          // Expanded layout - fixed-width sidebar (no resizing)
          <div className="flex h-full w-full">
            {/* Fixed Left Sidebar - No Resize Handle */}
            <div className="w-80 flex-shrink-0 bg-gray-100 dark:bg-zinc-800">
              <LeftSidebar 
                onCollapse={handleSidebarCollapse}
                showCollapseButton={true}
              />
            </div>
            
            {/* Main Content Area */}
            <div className="flex flex-col flex-1 min-w-0">
              <Header hasLeftSidebar={true} />
              <div className="flex-1 overflow-hidden">
                <ResizablePanelGroup
                  direction="horizontal"
                  className="flex flex-row h-full"
                >
                  <ResizablePanel
                    className="flex flex-col h-full w-full"
                    minSize={40}
                    defaultSize={sideView ? 70 : 100}
                  >
                    <div className="flex flex-row flex-grow overflow-auto">
                      {children}
                    </div>
                  </ResizablePanel>
                  {sideView ? <ElementSideView /> : <TaskList isMobile={false} />}
                </ResizablePanelGroup>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarProvider>
  );
};

export default Page;
